import { useCallback, useMemo } from 'react';

import StarSolid from '@simplywallst/ui-core/icons/StarSolid';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';

import { Dropdown } from '@/components/Dropdown';
import type { ButtonRenderProps } from '@/components/Dropdown';

import type { UserPeerSelection } from '../../selector';
import { DropdownButton } from './styled';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';

interface Props {
  id?: string;
  disabled?: boolean;
  selectedOption: UserPeerSelection;
  onSelect: (option: UserPeerSelection) => void;
}

const SavedPeersDropdown = ({
  id,
  disabled,
  selectedOption,
  onSelect,
}: Props) => {
  const handleSelect = useCallback(
    (option: string) => onSelect(option as UserPeerSelection),
    [onSelect]
  );
  const button = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
    }: ButtonRenderProps) => {
      const isSelected = selectedOption === value;
      return (
        <DropdownButton
          styleType="passive"
          alignIcon="right"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          disabled={disabled}
          data-cy-id={name}
          hasIcon={isSelected}
        >
          {isSelected && <StarSolid />}
          <span>{value}</span>
          <ChevronDownSmallSolid />
        </DropdownButton>
      );
    },
    [disabled, selectedOption]
  );

  const t = useValuationTranslation();

  const options = useMemo(
    () => [
      {
        option: 'saved' as const,
        label: t('vsPeers.savedPeers.saved'),
      },
      {
        option: 'default' as const,
        label: t('vsPeers.savedPeers.default'),
      },
      {
        option: 'edit' as const,
        label: t('vsPeers.savedPeers.edit'),
        icon: <EditSolid />,
        displayTopDivider: true,
      },
    ],
    [t]
  );

  return (
    <Dropdown
      name={`${id}-user-peer-selection`}
      align="right"
      displayType="inline-block"
      options={options}
      value={selectedOption}
      onSelect={handleSelect}
      buttonRender={button}
      showModalSm={false}
    />
  );
};

export default SavedPeersDropdown;
